import {
  Checkbox,
  Dialog,
  DialogMaxWidth,
  DialogVariant,
  Text,
  TextHighlight,
  TextSize,
  TextWeight
} from '@focaldata/cin-ui-components'
import { type FC, Fragment, useEffect, useMemo, useRef, useState } from 'react'
import useStyles from './PackageRestrictionsDialog.styles'
import { ConjointStateAttribute } from './PackageRestrictionsDialogProvider'

interface Props {
  open: boolean
  attributes: ConjointStateAttribute[]
  /** Number of packages (columns) per panel — only these are shown or editable. */
  panelSize: number
  /**
   * Last-saved allowedPackages per option id, 0-based — backend semantics:
   * null = unrestricted (eligible everywhere), [] = eligible nowhere (only
   * reachable via legacy/invalid data), [i, ...] = eligible only there.
   */
  savedAllowedPackages: Map<string, number[] | null>
  saving: boolean
  onClose: () => void
  onSave: (allowedPackagesByOptionId: Map<string, number[] | null>) => void
}

/**
 * Package-restriction editor. Packages are columns (1-based labels, matching
 * the package-labels feature), attribute values are rows. A checked box means
 * the value is allowed in that package; a value with every box checked is
 * unrestricted and saves as null, matching the backend (which treats an empty
 * allow-list as allowing nothing, not everything). Values keep any restriction
 * referencing a package beyond the current panel size untouched (nothing
 * needs pruning when panel size shrinks — see
 * useTransformDraftQuestionnaireToPreviewData / the respondent renderer,
 * which both ignore out-of-range indices) so shrinking and re-growing the panel
 * size doesn't lose data.
 */
const PackageRestrictionsDialog: FC<Props> = ({
  open,
  attributes,
  panelSize,
  savedAllowedPackages,
  saving,
  onClose,
  onSave
}) => {
  const { classes } = useStyles()

  const packageColumns = useMemo(
    () => Array.from({ length: panelSize }, (_, i) => i),
    [panelSize]
  )

  // In-range (currently displayed) checked state, editable via checkboxes.
  const [checkedByOptionId, setCheckedByOptionId] = useState<
    Map<string, Set<number>>
  >(new Map())
  // Out-of-range indices preserved untouched from the last save.
  const staleByOptionIdRef = useRef<Map<string, number[]>>(new Map())

  const initialisedRef = useRef(false)

  useEffect(() => {
    if (!open) {
      initialisedRef.current = false
      return
    }
    if (initialisedRef.current) {
      return
    }

    const checked = new Map<string, Set<number>>()
    const stale = new Map<string, number[]>()

    attributes.forEach((attribute) => {
      attribute.options.forEach((option) => {
        const saved = savedAllowedPackages.get(option.attributeOptionId) ?? null
        const isUnrestricted = saved == null
        const inRange = isUnrestricted
          ? []
          : saved.filter((i) => i >= 0 && i < panelSize)
        const outOfRange = isUnrestricted
          ? []
          : saved.filter((i) => i >= panelSize)

        checked.set(
          option.attributeOptionId,
          isUnrestricted ? new Set(packageColumns) : new Set(inRange)
        )
        stale.set(option.attributeOptionId, outOfRange)
      })
    })

    setCheckedByOptionId(checked)
    staleByOptionIdRef.current = stale
    initialisedRef.current = true
  }, [open, attributes, savedAllowedPackages, panelSize, packageColumns])

  // A value with zero in-range packages checked is ineligible for every
  // package that currently exists — regardless of any preserved out-of-range
  // history, which only matters again if panel size later grows back. Left
  // unblocked, it would either serialise as "unrestricted" (see handleSave,
  // when there's no stale history) or as an explicit list of exclusively
  // out-of-range indices (when there is) — both silently flip the user's
  // intent instead of saving "eligible nowhere".
  const unreachableOptionIds = useMemo(() => {
    const ids = new Set<string>()
    checkedByOptionId.forEach((checkedSet, optionId) => {
      if (checkedSet.size === 0) {
        ids.add(optionId)
      }
    })
    return ids
  }, [checkedByOptionId])

  const toggle = (attributeOptionId: string, packageIndex: number) => {
    setCheckedByOptionId((prev) => {
      const next = new Map(prev)
      const set = new Set(next.get(attributeOptionId) ?? [])
      if (set.has(packageIndex)) {
        set.delete(packageIndex)
      } else {
        set.add(packageIndex)
      }
      next.set(attributeOptionId, set)
      return next
    })
  }

  const handleSave = () => {
    const result = new Map<string, number[] | null>()
    checkedByOptionId.forEach((checkedSet, optionId) => {
      const stale = staleByOptionIdRef.current.get(optionId) ?? []
      const inRangeSorted = [...checkedSet].sort((a, b) => a - b)
      const isFullyUnrestricted =
        inRangeSorted.length === panelSize && stale.length === 0
      result.set(
        optionId,
        isFullyUnrestricted
          ? null
          : [...inRangeSorted, ...stale].sort((a, b) => a - b)
      )
    })
    onSave(result)
  }

  return (
    <Dialog
      title="Restrict attribute values to packages"
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={DialogMaxWidth.Lg}
      variant={DialogVariant.DoubleButton}
      secondaryButtonText="Cancel"
      secondaryButtonClick={onClose}
      primaryButtonText="Save Changes"
      primaryButtonClick={handleSave}
      primaryButtonLoading={saving}
      primaryButtonDisabled={unreachableOptionIds.size > 0}
    >
      <Text size={TextSize.ms}>
        Choose which packages each value can appear in. A value stays
        unrestricted — eligible for every package — until you deselect at least
        one.
      </Text>
      {unreachableOptionIds.size > 0 && (
        <Text size={TextSize.ms} highlight={TextHighlight.Error}>
          Select at least one package for every highlighted value below — a
          value can&apos;t be eligible in zero packages.
        </Text>
      )}

      <div className={classes.tableWrapper}>
        <div
          className={classes.grid}
          style={{
            gridTemplateColumns: `minmax(180px, 2fr) repeat(${panelSize}, minmax(90px, 1fr))`
          }}
        >
          <div className={classes.headerCell} />
          {packageColumns.map((packageIndex) => (
            <div
              key={packageIndex}
              className={classes.headerCell}
              data-testid={`package-column-header-${packageIndex}`}
            >
              <Text size={TextSize.ms} weight={TextWeight.SemiBold}>
                {`Package ${packageIndex + 1}`}
              </Text>
            </div>
          ))}

          {attributes.map((attribute) => (
            <Fragment key={attribute.attributeId}>
              <div className={classes.attributeNameCell}>
                <Text size={TextSize.ms} weight={TextWeight.SemiBold}>
                  {attribute.name || 'Untitled attribute'}
                </Text>
              </div>
              {packageColumns.map((packageIndex) => (
                <div key={packageIndex} className={classes.spacerCell} />
              ))}

              {attribute.options.map((option) => (
                <Fragment key={option.attributeOptionId}>
                  <div className={classes.valueCell}>
                    <Text
                      size={TextSize.ms}
                      highlight={
                        unreachableOptionIds.has(option.attributeOptionId)
                          ? TextHighlight.Error
                          : undefined
                      }
                    >
                      {option.value || 'Untitled value'}
                    </Text>
                  </div>
                  {packageColumns.map((packageIndex) => (
                    <div key={packageIndex} className={classes.checkboxCell}>
                      <Checkbox
                        checked={
                          checkedByOptionId
                            .get(option.attributeOptionId)
                            ?.has(packageIndex) ?? false
                        }
                        onChange={() =>
                          toggle(option.attributeOptionId, packageIndex)
                        }
                        ariaLabel={`Allow "${option.value}" in Package ${
                          packageIndex + 1
                        }`}
                      />
                    </div>
                  ))}
                </Fragment>
              ))}
            </Fragment>
          ))}
        </div>
      </div>
    </Dialog>
  )
}

export default PackageRestrictionsDialog
